/**
 * Enterprise Dashboard API
 * Pure HTTP transport layer with cancellation support and graceful error recovery.
 */
import api from '../../../lib/api';
import { dataUtils } from '../../../lib/dataUtils';
import { logger } from '../../../core/logger';
import { DashboardData, ExecutiveDashboardData } from '../types/dashboard.types';

export const DEFAULT_EXECUTIVE_SUMMARY: ExecutiveDashboardData = {
  societyId: 0,
  societyName: '',
  societyMode: 'RESIDENTIAL',
  finance: {
    monthlyCollection: 0,
    collectionTarget: 0,
    pendingDues: 0,
    defaultersCount: 0,
    monthlyExpense: 0,
    collectionRatePercent: 0,
    monthlyTrends: [],
  },
  security: {
    visitorsToday: 0,
    activeVisitorsNow: 0,
    expectedVisitors: 0,
    dailyHelpOnDuty: 0,
    guestCount: 0,
    deliveryCount: 0,
    cabCount: 0,
    serviceCount: 0,
    preApproved: 0,
    gateArrived: 0,
    verifiedInside: 0,
    checkedOut: 0,
  },
  helpdesk: {
    totalComplaints: 0,
    openComplaints: 0,
    inProgressComplaints: 0,
    resolvedComplaints: 0,
    breachedComplaints: 0,
    electricalCount: 0,
    plumbingCount: 0,
    liftCount: 0,
    securityCount: 0,
    cleanlinessCount: 0,
    generalCount: 0,
  },
  facility: {
    activeFacilities: 0,
    bookingsToday: 0,
    upcomingBookings: 0,
    peakHoursUtilizationPercent: 0,
    slotRequested: 0,
    confirmedPassIssued: 0,
    attended: 0,
  },
  community: {
    activeMarketplaceListings: 0,
    pendingOffers: 0,
    activeLostFoundPosts: 0,
    registeredBusinesses: 0,
    communityFeedItemsToday: 0,
  },
  governance: {
    apexCommitteeMembers: 0,
    activeTasks: 0,
    upcomingMeetings: 0,
    complianceHealthPercent: 0,
    fireNocValid: false,
    liftNocValid: false,
  },
  occupancy: {
    totalUnits: 0,
    ownerOccupiedUnits: 0,
    tenantOccupiedUnits: 0,
    vacantUnits: 0,
    occupancyRatePercent: 0,
  },
  personal: {
    myUnitNumber: '',
    myPendingDues: 0,
    myActiveComplaints: 0,
    myExpectedGuestsToday: 0,
    myUpcomingBookings: 0,
    myActiveListings: 0,
  },
};

export const dashboardApi = {
  /**
   * Fetch consolidated executive metrics for a specific society.
   * If societyId is not provided, returns default baseline summary without firing unnecessary requests.
   */
  getExecutiveDashboard: async (societyId: number | null, signal?: AbortSignal): Promise<ExecutiveDashboardData> => {
    if (!societyId) return DEFAULT_EXECUTIVE_SUMMARY;
    try {
      const res = await api.get(`/societies/${societyId}/dashboard/executive-summary`, {
        signal,
        skipGlobalErrorHandler: true,
      });
      const data = dataUtils.extractItem<ExecutiveDashboardData>(res.data);
      if (data && data.finance && data.security) {
        return data;
      }
      return DEFAULT_EXECUTIVE_SUMMARY;
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') throw err;
      logger.warn('Executive dashboard query fallback used', err);
      return DEFAULT_EXECUTIVE_SUMMARY;
    }
  },

  /**
   * Fetch aggregated baseline dashboard data across entities.
   */
  getDashboardData: async (societyId?: number | null, signal?: AbortSignal): Promise<DashboardData> => {
    const isAuthenticated = localStorage.getItem('xservices_authenticated');
    if (!isAuthenticated) throw new Error('Not authenticated');

    const storedSocietyId = Number(localStorage.getItem('xservices_society_id')) || null;
    const targetSocietyId = societyId ?? storedSocietyId;

    const membersUrl = targetSocietyId ? `/members?societyId=${targetSocietyId}&size=1&page=0` : null;
    const transactionsUrl = targetSocietyId ? `/transactions/society/${targetSocietyId}?size=500` : null;

    try {
      const [societiesRes, membersRes, transactionsRes] = await Promise.allSettled([
        api.get('/societies', { signal }),
        membersUrl ? api.get(membersUrl, { signal }) : Promise.reject(new Error('No society')),
        transactionsUrl ? api.get(transactionsUrl, { signal }) : Promise.reject(new Error('No society')),
      ]);

      const societiesData = societiesRes.status === 'fulfilled'
        ? dataUtils.extractData<any[]>(societiesRes.value.data)
        : [];

      let activeMemberCount = 0;
      if (membersRes.status === 'fulfilled') {
        const raw = membersRes.value.data;
        const body = raw?.data ?? raw;
        if (typeof body?.totalElements === 'number') {
          activeMemberCount = body.totalElements;
        } else {
          const arr = dataUtils.extractData<any[]>(raw);
          activeMemberCount = Array.isArray(arr) ? arr.length : 0;
        }
      }

      const transactionsData = transactionsRes.status === 'fulfilled'
        ? dataUtils.extractData<any[]>(transactionsRes.value.data)
        : [];

      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      const currentMonthTxns = (transactionsData as any[]).filter((t: any) => {
        const dt = new Date(t?.transactionDateTime || t?.createdAt || 0);
        return dt.getFullYear() === currentYear && dt.getMonth() + 1 === currentMonth;
      });

      const monthlyIncome = currentMonthTxns
        .filter((t: any) => t?.transactionDirection === 'CREDIT')
        .reduce((sum: number, t: any) => sum + (Number(t?.amount) || 0), 0);

      const monthlyExpense = currentMonthTxns
        .filter((t: any) => t?.transactionDirection === 'DEBIT')
        .reduce((sum: number, t: any) => sum + (Number(t?.amount) || 0), 0);

      return {
        stats: {
          societies: societiesData.length,
          members: activeMemberCount,
          income: monthlyIncome,
          expense: monthlyExpense,
        },
        chartData: [
          { name: 'This Month', income: monthlyIncome, expense: monthlyExpense },
        ],
        activities: [],
      };
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') throw err;
      logger.warn('Dashboard data fetch error', err);
      return {
        stats: { societies: 0, members: 0, income: 0, expense: 0 },
        chartData: [],
        activities: [],
      };
    }
  },
};
